import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { CONTACTS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_TYPES = ["Corporate Team", "Family & Friends", "School / University", "Celebration / Event"];
const EXPERIENCES = [
  "Day Outing",
  "Adventure Zone",
  "Battle Zone",
  "Team Building Program",
  "Camping Night",
  "Corporate Training",
  "Custom Event",
];

const inputCls =
  "w-full bg-night border border-cream/15 text-cream placeholder:text-cream/30 px-5 py-4 text-sm focus:outline-none focus:border-amber transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", contact: "", group_type: GROUP_TYPES[0], experience: EXPERIENCES[0], message: "", marketing_consent: false });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      toast.error("Please add your name and a contact number or email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received — our team will reach out shortly.");
      setForm({ name: "", contact: "", group_type: GROUP_TYPES[0], experience: EXPERIENCES[0], message: "", marketing_consent: false });
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-night py-28 md:py-40 border-t border-cream/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-amber mb-6" data-testid="contact-label">
            Begin the Adventure
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight text-cream leading-[1.05] mb-8">
            Your team is <span className="italic text-amber">one call away</span> from the wild.
          </h2>
          <p className="text-base md:text-lg text-cream/70 leading-relaxed mb-12 max-w-md">
            Day outings, overnight camps, leadership bootcamps or full-company takeovers — tell us what
            you're dreaming of and we'll design it.
          </p>

          <div className="space-y-4" data-testid="contact-whatsapp-list">
            {CONTACTS.map((c) => (
              <a
                key={c.label}
                data-testid={`whatsapp-link-${c.label.replace(/ /g, "-")}`}
                href={c.wa}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 border border-cream/15 p-5 hover:border-amber transition-colors duration-300"
              >
                <MessageCircle className="text-amber" size={20} />
                <span className="text-sm tracking-[0.15em] text-cream/80 group-hover:text-amber transition-colors duration-300">
                  WhatsApp or Call — {c.label}
                </span>
              </a>
            ))}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="inquiry-form"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 bg-forest border border-cream/10 p-8 md:p-12 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-amber mb-3">Your Name</label>
              <input
                data-testid="inquiry-name-input"
                className={inputCls}
                placeholder="Amaya Perera"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-amber mb-3">Phone or Email</label>
              <input
                data-testid="inquiry-contact-input"
                className={inputCls}
                placeholder="07X XXX XXXX"
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-amber mb-3">Who's Coming</label>
              <select
                data-testid="inquiry-group-type-select"
                className={inputCls}
                value={form.group_type}
                onChange={(e) => setForm({ ...form, group_type: e.target.value })}
              >
                {GROUP_TYPES.map((g) => (
                  <option key={g} value={g} className="bg-night">{g}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-amber mb-3">Dream Experience</label>
              <select
                data-testid="inquiry-experience-select"
                className={inputCls}
                value={form.experience}
                onChange={(e) => setForm({ ...form, experience: e.target.value })}
              >
                {EXPERIENCES.map((x) => (
                  <option key={x} value={x} className="bg-night">{x}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-amber mb-3">Tell Us More</label>
            <textarea
              data-testid="inquiry-message-textarea"
              className={`${inputCls} min-h-[140px] resize-y`}
              placeholder="Group size, dates, occasions, wild ideas..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </div>

          <label className="flex items-start gap-3 text-xs text-cream/50 cursor-pointer" data-testid="inquiry-consent">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5 accent-[#EAA332]" />
            Keep me updated with LUSH Adventure offers and news by email (optional)
          </label>

          <button
            data-testid="inquiry-submit-button"
            type="submit"
            disabled={sending}
            className="group flex items-center justify-center gap-3 w-full bg-amber text-night text-xs tracking-[0.25em] uppercase font-semibold px-8 py-5 hover:bg-cream transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Sending..." : "Send Inquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
